import { Contract } from '@ethersproject/contracts'
import { abi as IUniswapV2PairABI } from '@uniswap/v2-core/build/IUniswapV2Pair.json'
import { useMemo } from 'react'
import { ERC20_BYTES32_ABI } from '../constants/abis/erc20'
import ERC20_ABI from '../constants/abis/erc20.json'
import WETH_ABI from '../constants/abis/weth.json'
import { MULTICALL_ABI, MULTICALL_NETWORKS } from '../constants/multicall'
import { WRAPPED_NATIVE } from '../chainConfig'
import { getContract } from '../utils'
import { useActiveWeb3React, useReadWeb3React } from './index'

// returns null on errors
function useContract(address?: string, ABI?: any, withSignerIfPossible = true): Contract | null {
  const { library, account } = useActiveWeb3React()

  return useMemo(() => {
    if (!address || !ABI || !library) return null
    try {
      return getContract(address, ABI, library, withSignerIfPossible && account ? account : undefined)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [address, ABI, library, withSignerIfPossible, account])
}

function useReadContract(address?: string, ABI?: any): Contract | null {
  const { library } = useReadWeb3React()

  return useMemo(() => {
    if (!address || !ABI || !library) return null
    try {
      return getContract(address, ABI, library)
    } catch (error) {
      console.error('Failed to get read contract', error)
      return null
    }
  }, [address, ABI, library])
}

export function useTokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  const readContract = useReadContract(tokenAddress, ERC20_ABI)
  const signerContract = useContract(tokenAddress, ERC20_ABI, withSignerIfPossible)
  return withSignerIfPossible === false ? readContract : signerContract
}

export function useWETHContract(withSignerIfPossible?: boolean): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? WRAPPED_NATIVE.address : undefined, WETH_ABI, withSignerIfPossible)
}

export function useBytes32TokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  const readContract = useReadContract(tokenAddress, ERC20_BYTES32_ABI)
  const signerContract = useContract(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible)
  return withSignerIfPossible === false ? readContract : signerContract
}

export function usePairContract(pairAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(pairAddress, IUniswapV2PairABI, withSignerIfPossible)
}

export function useMulticallContract(): Contract | null {
  const { chainId } = useReadWeb3React()
  return useReadContract(chainId && MULTICALL_NETWORKS[chainId], MULTICALL_ABI)
}
